import { useEffect, useMemo, useState } from "react";
import {
  ScanSearch,
  Image as ImageIcon,
  Sparkles,
  ShieldCheck,
  Wrench,
  SearchCheck,
  AlertTriangle,
  CheckCircle2,
  Gauge,
  Upload,
  Loader2,
  Trash2,
} from "lucide-react";

type DiagnosisResult = {
  issueTitle: string;
  diagnosis: string;
  likelyCauses: string[];
  recommendedChecks: string[];
  recommendedFixes: string[];
  confidence: number;
  notes: string;
};

type PreparedImagePayload = {
  base64: string;
  mimeType: string;
};

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
        <span className="text-dtf-accent">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <BluePanel>
      <div className="p-5">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-accent">
            {icon}
          </div>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
        </div>
        <p className="text-sm leading-7 text-dtf-text-muted">{description}</p>
      </div>
    </BluePanel>
  );
}

function SectionCard({
  title,
  icon,
  items,
}: {
  title: string;
  icon: React.ReactNode;
  items: string[];
}) {
  return (
    <BluePanel>
      <div className="p-5">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-accent">
            {icon}
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>

        {items.length ? (
          <ul className="space-y-3">
            {items.map((item, index) => (
              <li
                key={`${title}-${index}`}
                className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm leading-7 text-dtf-text"
              >
                {item}
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 px-4 py-4 text-sm text-dtf-text-muted">
            No items returned.
          </div>
        )}
      </div>
    </BluePanel>
  );
}

function confidenceLabel(confidence: number) {
  if (confidence >= 0.85) return "High";
  if (confidence >= 0.65) return "Medium";
  return "Low";
}

function confidencePercent(confidence: number) {
  return `${Math.round((confidence || 0) * 100)}%`;
}

function normalizeResult(raw: any): DiagnosisResult | null {
  if (!raw) return null;

  if (raw.result) {
    return {
      issueTitle: raw.result.issueTitle ?? "Unknown Issue",
      diagnosis: raw.result.diagnosis ?? "",
      likelyCauses: Array.isArray(raw.result.likelyCauses) ? raw.result.likelyCauses : [],
      recommendedChecks: Array.isArray(raw.result.recommendedChecks)
        ? raw.result.recommendedChecks
        : [],
      recommendedFixes: Array.isArray(raw.result.recommendedFixes)
        ? raw.result.recommendedFixes
        : [],
      confidence: typeof raw.result.confidence === "number" ? raw.result.confidence : 0,
      notes: raw.result.notes ?? "",
    };
  }

  return null;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read selected image."));
    reader.readAsDataURL(file);
  });
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load selected image."));
    img.src = dataUrl;
  });
}

async function fileToJpegBase64(file: File): Promise<PreparedImagePayload> {
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImage(dataUrl);

  const maxDimension = 1600;
  const longestSide = Math.max(img.width, img.height);
  const scale = longestSide > maxDimension ? maxDimension / longestSide : 1;

  const targetWidth = Math.max(1, Math.round(img.width * scale));
  const targetHeight = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not prepare image for upload.");
  }

  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  const jpegDataUrl = canvas.toDataURL("image/jpeg", 0.82);
  const base64 = jpegDataUrl.includes(",") ? jpegDataUrl.split(",")[1] : jpegDataUrl;

  if (!base64) {
    throw new Error("Prepared image was empty.");
  }

  return {
    base64,
    mimeType: "image/jpeg",
  };
}

export default function DiagnosticAssistantPage() {
  const [userMessage, setUserMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [rawResponse, setRawResponse] = useState<any>(null);
  const [result, setResult] = useState<DiagnosisResult | null>(null);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const stats = useMemo(() => {
    return {
      imageLoaded: selectedFile ? "Loaded" : "Optional",
      messageReady: userMessage.trim() ? "Ready" : "Add details",
      status: loading ? "Analyzing" : result ? "Complete" : "Waiting",
      confidence: result ? confidencePercent(result.confidence) : "--",
    };
  }, [selectedFile, userMessage, loading, result]);

  const handleFileChange = (file: File | null) => {
    setErrorMessage("");
    setResult(null);
    setRawResponse(null);

    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview("");
    }

    setSelectedFile(file);

    if (!file) {
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const clearAll = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }

    setUserMessage("");
    setSelectedFile(null);
    setImagePreview("");
    setErrorMessage("");
    setRawResponse(null);
    setResult(null);
  };

  const handleAnalyze = async () => {
    setLoading(true);
    setErrorMessage("");
    setRawResponse(null);
    setResult(null);

    try {
      if (!userMessage.trim() && !selectedFile) {
        setErrorMessage("Add a description, an image, or both before analyzing.");
        return;
      }

      let imageBase64 = "";
      let mimeType = "";

      if (selectedFile) {
        const preparedImage = await fileToJpegBase64(selectedFile);
        imageBase64 = preparedImage.base64;
        mimeType = preparedImage.mimeType;
      }

      const response = await fetch("/.netlify/functions/gemini-diagnose", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userMessage: userMessage.trim(),
          imageBase64,
          mimeType,
        }),
      });

      let data: any = null;

      try {
        data = await response.json();
      } catch {
        data = null;
      }

      setRawResponse(data);

      if (!response.ok) {
        setErrorMessage(
          data?.details ||
            data?.error ||
            "Analysis failed. Please try again with a clearer image or shorter description."
        );
        return;
      }

      if (data?.unparsed && data?.rawText) {
        setErrorMessage(
          "The AI responded, but the format was not usable. Try again with a clearer image or description."
        );
        return;
      }

      const parsed = normalizeResult(data);
      if (!parsed) {
        setErrorMessage("No usable diagnosis was returned.");
        return;
      }

      setResult(parsed);
    } catch (error: any) {
      setErrorMessage(error?.message || "Something went wrong during analysis.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 text-white sm:px-6 lg:px-8">
      <BluePanel className="mb-6">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
                <ScanSearch className="h-3.5 w-3.5" />
                Smart Diagnostic Assistant
              </div>

              <h1 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                Smart Scan
              </h1>

              <p className="text-lg leading-8 text-dtf-text">
                Upload a print photo, describe what you are seeing, and get a structured
                DTF diagnosis with likely causes, recommended checks, and next-step fixes.
              </p>
            </div>

            <div className="grid gap-3 sm:min-w-[320px] sm:grid-cols-2">
              <StatPill
                icon={<ImageIcon className="h-4 w-4" />}
                label="Image"
                value={stats.imageLoaded}
              />
              <StatPill
                icon={<SearchCheck className="h-4 w-4" />}
                label="Input"
                value={stats.messageReady}
              />
              <StatPill
                icon={<Sparkles className="h-4 w-4" />}
                label="Status"
                value={stats.status}
              />
              <StatPill
                icon={<Gauge className="h-4 w-4" />}
                label="Confidence"
                value={stats.confidence}
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <div className="rounded-full border border-white/10 bg-black/20 px-4 py-2 text-white">
              Built for real DTF troubleshooting
            </div>
            <div className="rounded-full border border-white/10 bg-black/20 px-4 py-2 text-white">
              Image + symptom-based analysis
            </div>
            <div className="rounded-full border border-white/10 bg-black/20 px-4 py-2 text-white">
              Structured results, not vague guesses
            </div>
          </div>
        </div>
      </BluePanel>

      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <FeatureCard
          icon={<ImageIcon className="h-5 w-5" />}
          title="Scan print issues"
          description="Upload a real image from your print, transfer, or result and let the assistant inspect visible patterns and symptoms."
        />
        <FeatureCard
          icon={<ShieldCheck className="h-5 w-5" />}
          title="Get structured answers"
          description="See a likely issue title, diagnosis summary, causes, recommended checks, and next-step fixes in one clean result."
        />
        <FeatureCard
          icon={<Wrench className="h-5 w-5" />}
          title="Troubleshoot faster"
          description="Use the assistant to narrow down what to check next instead of guessing and wasting film, ink, time, and presses."
        />
      </div>

      <BluePanel className="mb-6">
        <div className="border-b border-white/8 px-6 py-5">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
            <Upload className="h-3.5 w-3.5" />
            Upload and analyze
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-white">
            Submit a problem for review
          </h2>

          <p className="mt-2 max-w-3xl text-dtf-text-muted">
            Add a photo if you have one. The more specific your description is, the better
            the result will be.
          </p>
        </div>

        <div className="grid gap-6 px-6 py-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-semibold uppercase tracking-[0.16em] text-dtf-text-muted">
                Describe the issue
              </label>
              <textarea
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                placeholder="Example: White ink is slightly shadowing left of the color layer, and it seems more noticeable on smaller text..."
                className="min-h-[180px] w-full rounded-[24px] border border-white/10 bg-black/20 px-5 py-4 text-sm leading-7 text-white outline-none transition placeholder:text-dtf-text-muted focus:border-dtf-blue/30 focus:ring-2 focus:ring-dtf-blue/20"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleAnalyze}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/30 bg-dtf-blue/10 px-5 py-3 text-sm font-semibold text-white transition hover:border-dtf-blue/40 hover:bg-dtf-blue/15 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <ScanSearch className="h-4 w-4" />
                    Analyze Issue
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={clearAll}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-5 py-3 text-sm font-semibold text-dtf-text transition hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Trash2 className="h-4 w-4" />
                Clear
              </button>
            </div>

            {errorMessage ? (
              <div className="rounded-[24px] border border-red-500/20 bg-red-500/10 px-4 py-4 text-sm leading-7 text-red-200">
                {errorMessage}
              </div>
            ) : null}
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-semibold uppercase tracking-[0.16em] text-dtf-text-muted">
                Upload image
              </label>

              <label className="flex min-h-[260px] cursor-pointer flex-col items-center justify-center rounded-[24px] border border-dashed border-dtf-blue/25 bg-black/20 px-6 py-8 text-center transition hover:border-dtf-blue/40">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                />

                {imagePreview ? (
                  <div className="w-full">
                    <img
                      src={imagePreview}
                      alt="Selected upload preview"
                      className="mx-auto max-h-[240px] w-full rounded-[20px] border border-white/10 object-contain"
                    />
                    <div className="mt-3 text-sm text-dtf-text-muted">
                      {selectedFile?.name}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-dtf-accent">
                      <Upload className="h-6 w-6" />
                    </div>
                    <div className="text-base font-semibold text-white">
                      Click to upload an image
                    </div>
                    <div className="mt-2 max-w-sm text-sm leading-7 text-dtf-text-muted">
                      Add a photo of the print, transfer, or defect pattern. Image upload is optional, but recommended when possible.
                    </div>
                  </>
                )}
              </label>
            </div>

            <div className="rounded-[24px] border border-white/10 bg-black/20 px-4 py-4 text-sm leading-7 text-dtf-text-muted">
              Best results come from clear images with good lighting, tight crop on the
              defect area, and a short description of what you are seeing.
            </div>
          </div>
        </div>
      </BluePanel>

      <BluePanel className="mb-6">
        <div className="flex items-center justify-between gap-4 px-6 py-5">
          <div>
            <div className="text-sm text-dtf-text-muted">Analysis results</div>
            <div className="text-2xl font-bold text-white">
              {result ? result.issueTitle : "Waiting for scan"}
            </div>
          </div>

          <div className="rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-dtf-blue">
            {result ? `${confidenceLabel(result.confidence)} confidence` : "No result yet"}
          </div>
        </div>
      </BluePanel>

      {result ? (
        <>
          <BluePanel className="mb-6">
            <div className="p-6">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-accent/20 bg-black/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-accent">
                <AlertTriangle className="h-3.5 w-3.5" />
                Diagnosis summary
              </div>

              <h2 className="mb-3 text-3xl font-bold tracking-tight text-white">
                {result.issueTitle}
              </h2>

              <p className="text-base leading-8 text-dtf-text">{result.diagnosis}</p>

              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <StatPill
                  icon={<Gauge className="h-4 w-4" />}
                  label="Confidence"
                  value={confidencePercent(result.confidence)}
                />
                <StatPill
                  icon={<SearchCheck className="h-4 w-4" />}
                  label="Likely Causes"
                  value={`${result.likelyCauses.length}`}
                />
                <StatPill
                  icon={<CheckCircle2 className="h-4 w-4" />}
                  label="Recommended Fixes"
                  value={`${result.recommendedFixes.length}`}
                />
              </div>

              {result.notes ? (
                <div className="mt-5 rounded-[22px] border border-white/10 bg-black/20 px-4 py-4 text-sm leading-7 text-dtf-text-muted">
                  <span className="font-semibold text-white">Notes:</span> {result.notes}
                </div>
              ) : null}
            </div>
          </BluePanel>

          <div className="mb-6 grid gap-5 md:grid-cols-3">
            <SectionCard
              icon={<AlertTriangle className="h-5 w-5" />}
              title="Likely Causes"
              items={result.likelyCauses}
            />
            <SectionCard
              icon={<SearchCheck className="h-5 w-5" />}
              title="Recommended Checks"
              items={result.recommendedChecks}
            />
            <SectionCard
              icon={<Wrench className="h-5 w-5" />}
              title="Recommended Fixes"
              items={result.recommendedFixes}
            />
          </div>
        </>
      ) : (
        <BluePanel>
          <div className="p-6 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-text-muted">
              <Sparkles className="h-3.5 w-3.5" />
              Ready when you are
            </div>
            <h2 className="text-2xl font-bold text-white">No scan results yet</h2>
            <p className="mx-auto mt-3 max-w-2xl text-dtf-text-muted">
              Upload an image, describe the issue, and run a scan to see a structured DTF diagnosis here.
            </p>
          </div>
        </BluePanel>
      )}

      {rawResponse?.unparsed ? (
        <BluePanel className="mt-6">
          <div className="p-6">
            <div className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-dtf-accent">
              Debug response
            </div>
            <pre className="overflow-x-auto rounded-[20px] border border-white/10 bg-black/30 p-4 text-xs leading-6 text-dtf-text-muted">
              {rawResponse.rawText}
            </pre>
          </div>
        </BluePanel>
      ) : null}
    </div>
  );
}